import { datadogRum } from "@datadog/browser-rum";
import { RiLoopRightLine } from "@remixicon/react";
import { useAtomValue } from "jotai";
import { type PropsWithChildren, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAccountData, useAppInit, useAuth } from "@/hooks";
import { UserHasNoOrganisationPage } from "@/pages/UserHasNoOrganisationPage";
import { userAtom } from "@/store";
import { currentAccountAtom } from "@/store/atoms/currentAccount";

export const AuthenticateGuard = ({ children }: PropsWithChildren) => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { isAuthenticated } = useAuth();
  const { isLoadingUserData, isLoadingAccounts, currentUserData } =
    useAppInit();
  const { isLoading: isLoadingAccountData, hasNoWorkspace } = useAccountData();

  const user = useAtomValue(userAtom);
  const currentAccount = useAtomValue(currentAccountAtom);

  const isUserAtomPending =
    isAuthenticated && !isLoadingUserData && user === undefined;
  const isAccountAtomPending =
    isAuthenticated && !isLoadingAccounts && currentAccount === undefined;

  useEffect(() => {
    if (
      isAuthenticated &&
      currentUserData?.id &&
      datadogRum.getUser()?.id !== currentUserData?.id
    ) {
      datadogRum.setUser({
        name: `${currentUserData?.firstName} ${currentUserData?.lastName}`,
        email: currentUserData?.email,
        id: currentUserData?.id,
      });

      // Update DataLayer (GTM) user info
      window.dataLayer.push({
        event: "login",
        userId: currentUserData.id,
        email: currentUserData.email,
      });
    }
  }, [isAuthenticated, currentUserData]);

  useEffect(() => {
    if (isLoadingUserData) {
      return;
    }

    if (!isAuthenticated && !pathname.startsWith("/auth")) {
      setIsRedirecting(true);
      void navigate("/auth/sign-in");
    } else if (isAuthenticated && pathname.startsWith("/auth")) {
      setIsRedirecting(true);
      void navigate("/");
    }
  }, [isAuthenticated, isLoadingUserData, navigate, pathname]);

  const needsAuthRedirect =
    !isLoadingUserData && !isAuthenticated && !pathname.startsWith("/auth");

  if (
    isLoadingUserData ||
    isLoadingAccounts ||
    isUserAtomPending ||
    isAccountAtomPending ||
    isLoadingAccountData ||
    isRedirecting ||
    needsAuthRedirect
  ) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <RiLoopRightLine className="block size-si-sm animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && hasNoWorkspace) {
    return <UserHasNoOrganisationPage />;
  }

  return children;
};
